/**
 * client.ts — typed fetch client for the Helm control plane (SPEC §2/§3).
 *
 * - Bearer access token (15-min) on every call; transparent refresh (12-h) on
 *   401 via POST /auth/refresh, with single-flight to avoid refresh stampedes.
 * - All paths are under /api/v1 (SPEC §0/§2/§3).
 * - The token store is pluggable so auth state lives in one place (src/auth/store).
 */

import type {
  AgentRelease,
  AgentUpdateResult,
  AgentUpdateBulkResult,
  AuditEvent,
  AuditVerify,
  AuthConfig,
  CARootPubKey,
  Device,
  DeviceGroup,
  InstallerConfig,
  InstallerResult,
  LaunchToken,
  LoginPasswordResult,
  Org,
  Recording,
  Session,
  SessionDetail,
  SessionEvent,
  SessionMode,
  SessionStartResult,
  SupportCode,
  SysInfoSnapshot,
  TokenPair,
  TurnCredentials,
  User,
  WebAuthnLoginBegin,
  WebAuthnRegisterBegin,
  WebAuthnRegisterFinish,
} from '@/types';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const PREFIX = '/api/v1';

export interface TokenStore {
  getAccess(): string | null;
  getRefresh(): string | null;
  setTokens(t: TokenPair): void;
  clear(): void;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private refreshing: Promise<void> | null = null;

  constructor(
    private readonly tokens: TokenStore,
    private readonly onAuthLost: () => void = () => {},
  ) {}

  // ---- core request -------------------------------------------------------
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    opts: {
      auth?: boolean;
      query?: Record<string, string | undefined>;
      // Explicit bearer token to use INSTEAD of the store (first-run passkey
      // registration uses the short-lived register token, which is not — and must
      // not be — persisted as the session access token). When set, the transparent
      // refresh-on-401 path is skipped (there is no refresh token for it).
      bearer?: string;
    } = {},
  ): Promise<T> {
    const auth = opts.auth ?? true;
    const url = this.buildUrl(path, opts.query);

    const doFetch = async (): Promise<Response> => {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      if (opts.bearer) {
        headers.Authorization = `Bearer ${opts.bearer}`;
      } else if (auth) {
        const access = this.tokens.getAccess();
        if (access) headers.Authorization = `Bearer ${access}`;
      }
      return fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'same-origin',
      });
    };

    let res = await doFetch();
    if (res.status === 401 && auth && !opts.bearer && this.tokens.getRefresh()) {
      await this.refresh();
      res = await doFetch();
    }
    return this.parse<T>(res);
  }

  private buildUrl(path: string, query?: Record<string, string | undefined>): string {
    const url = new URL(`${API_BASE}${PREFIX}${path}`, window.location.origin);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined) url.searchParams.set(k, v);
      }
    }
    return url.toString();
  }

  private async parse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const json = text ? safeJson(text) : undefined;
    if (!res.ok) {
      const code = (json as { code?: string } | undefined)?.code ?? `http_${res.status}`;
      const message = (json as { message?: string } | undefined)?.message ?? res.statusText;
      if (res.status === 401) this.onAuthLost();
      throw new ApiError(res.status, code, message);
    }
    return json as T;
  }

  /** Single-flight refresh: concurrent 401s share one refresh round-trip. */
  private async refresh(): Promise<void> {
    if (!this.refreshing) {
      this.refreshing = (async () => {
        const refresh = this.tokens.getRefresh();
        if (!refresh) throw new ApiError(401, 'no_refresh', 'no refresh token');
        const res = await fetch(this.buildUrl('/auth/refresh'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh }),
        });
        if (!res.ok) {
          this.tokens.clear();
          this.onAuthLost();
          throw new ApiError(res.status, 'refresh_failed', 'token refresh failed');
        }
        // /auth/refresh returns a NEW access token only ({access}); the refresh
        // token is unchanged (server handlers_auth.go handleRefresh). Preserve the
        // existing refresh so we don't clobber it with undefined.
        const body = (await res.json()) as { access: string };
        this.tokens.setTokens({ access: body.access, refresh });
      })().finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  // ---- §2 Auth ------------------------------------------------------------
  // Public, unauthenticated probe: which sign-in methods does this deployment
  // offer? Drives whether the console shows the Microsoft button and/or hides
  // the password form (server handlers_oidc.go handleAuthConfig).
  getAuthConfig(): Promise<AuthConfig> {
    return this.request('GET', '/auth/config', undefined, { auth: false });
  }

  // The server resolves the tenant by org slug so the login form is tenant-aware
  // without leaking an org id (server handlers_auth.go loginRequest.org_slug).
  login(orgSlug: string, email: string, password: string): Promise<LoginPasswordResult> {
    return this.request(
      'POST',
      '/auth/login',
      { org_slug: orgSlug, email, password },
      { auth: false },
    );
  }

  // /auth/webauthn/login/begin resolves org+email to an assertion (server takes
  // org_slug + email; the challenge_id is implied by the email + stored options).
  webauthnLoginBegin(orgSlug: string, email: string): Promise<WebAuthnLoginBegin> {
    return this.request(
      'POST',
      '/auth/webauthn/login/begin',
      { org_slug: orgSlug, email },
      { auth: false },
    );
  }

  // The server reads challenge_id from the QUERY STRING and parses the raw
  // assertion from the body (go-webauthn FinishLogin reads the whole request).
  webauthnLoginFinish(challengeId: string, assertion: unknown): Promise<TokenPair> {
    return this.request(
      'POST',
      '/auth/webauthn/login/finish',
      assertion,
      { auth: false, query: { challenge_id: challengeId } },
    );
  }

  // OIDC→passkey step-up: `bearer` is the short-lived step-up token the OIDC
  // callback handed back in the URL fragment (a passkey-holding account just
  // passed the Microsoft first factor). The server returns a FIRST-FACTOR-VERIFIED
  // assertion challenge; complete it at webauthnLoginFinish to get console tokens.
  webauthnStepUpBegin(bearer: string): Promise<WebAuthnLoginBegin> {
    return this.request('POST', '/auth/webauthn/stepup/begin', {}, { auth: false, bearer });
  }

  // `bearer` overrides the stored access token — used for the first-run ceremony,
  // where the only credential is the short-lived registration token (codex High
  // #5). Omit it for a logged-in user adding another passkey.
  webauthnRegisterBegin(bearer?: string): Promise<WebAuthnRegisterBegin> {
    return this.request('POST', '/auth/webauthn/register/begin', {}, { bearer });
  }

  // Like login/finish: the server reads challenge_id from the QUERY STRING and
  // parses the raw credential from the body (go-webauthn FinishRegistration). On a
  // first-run ceremony the response carries full console tokens (access+refresh).
  webauthnRegisterFinish(
    challengeId: string,
    credential: unknown,
    bearer?: string,
  ): Promise<WebAuthnRegisterFinish> {
    return this.request('POST', '/auth/webauthn/register/finish', credential, {
      query: { challenge_id: challengeId },
      bearer,
    });
  }

  refreshTokens(): Promise<void> {
    return this.refresh();
  }

  async logout(): Promise<void> {
    const refresh = this.tokens.getRefresh();
    try {
      await this.request<void>('POST', '/auth/logout', { refresh });
    } finally {
      this.tokens.clear();
    }
  }

  // ---- §3 Orgs ------------------------------------------------------------
  // NOTE: the server wraps every LIST response in a named key
  // ({orgs|users|devices|groups|sessions:[...]}) and composite detail responses
  // in an object ({device,last_sysinfo}, {session,events}, {events,verify}). We
  // unwrap here so callers keep the SPEC DTO shapes. Single-resource GET/POST
  // (getOrg/getUser/getDevice-rename...) return the bare object — no unwrap.
  async listOrgs(orgId?: string): Promise<Org[]> {
    const r = await this.request<{ orgs: Org[] }>('GET', '/orgs', undefined, {
      query: { org_id: orgId },
    });
    return r.orgs ?? [];
  }
  createOrg(input: { name: string; slug: string; parent_org_id?: string }): Promise<Org> {
    return this.request('POST', '/orgs', input);
  }
  getOrg(id: string): Promise<Org> {
    return this.request('GET', `/orgs/${id}`);
  }
  updateOrg(id: string, patch: Partial<Pick<Org, 'name' | 'branding' | 'policy'>>): Promise<Org> {
    return this.request('PATCH', `/orgs/${id}`, patch);
  }
  deleteOrg(id: string): Promise<void> {
    return this.request('DELETE', `/orgs/${id}`);
  }

  // ---- §3 Users -----------------------------------------------------------
  async listUsers(orgId?: string): Promise<User[]> {
    const r = await this.request<{ users: User[] }>('GET', '/users', undefined, {
      query: { org_id: orgId },
    });
    return r.users ?? [];
  }
  createUser(input: { email: string; display_name?: string; role: User['role'] }): Promise<User> {
    return this.request('POST', '/users', input);
  }
  getUser(id: string): Promise<User> {
    return this.request('GET', `/users/${id}`);
  }
  updateUser(id: string, patch: Partial<Pick<User, 'display_name' | 'role' | 'status'>>): Promise<User> {
    return this.request('PATCH', `/users/${id}`, patch);
  }
  deleteUser(id: string): Promise<void> {
    return this.request('DELETE', `/users/${id}`);
  }

  // ---- §3 Devices ---------------------------------------------------------
  async listDevices(orgId?: string): Promise<Device[]> {
    const r = await this.request<{ devices: Device[] }>('GET', '/devices', undefined, {
      query: { org_id: orgId },
    });
    return r.devices ?? [];
  }
  async getDevice(id: string): Promise<Device> {
    // Detail composite: { device, last_sysinfo }. Fold last_sysinfo into the DTO.
    const r = await this.request<{ device: Device; last_sysinfo?: SysInfoSnapshot | null }>(
      'GET',
      `/devices/${id}`,
    );
    return { ...r.device, last_sysinfo: r.last_sysinfo ?? null };
  }
  updateDevice(id: string, patch: { name?: string }): Promise<Device> {
    return this.request('PATCH', `/devices/${id}`, patch);
  }
  deleteDevice(id: string): Promise<void> {
    return this.request('DELETE', `/devices/${id}`);
  }

  // ---- Agent updates "from RemoteHelm" (auto-update Phase B) ---------------
  // The published agent build the deployment serves as "latest". The console
  // compares each device's agent_version against this to badge outdated agents.
  getAgentRelease(): Promise<AgentRelease> {
    return this.request('GET', '/agent-release');
  }
  // Push an on-demand self-update to ONE online device (org_admin+). The agent
  // downloads + verifies + swaps the signed build; a no-op if already current.
  updateAgent(id: string): Promise<AgentUpdateResult> {
    return this.request('POST', `/devices/${id}/agent-update`, {});
  }
  // Push the published update to every online, out-of-date agent in the org.
  updateAgentsBulk(): Promise<AgentUpdateBulkResult> {
    return this.request('POST', '/devices/agent-update', {});
  }

  // C1 (SPEC §10.1): the CA root that signs (device_id, noise_pubkey) attestations.
  // Returns the served root public key as hex DER SPKI (ECDSA P-256). When a CA
  // root is pinned out-of-band (VITE_HELM_CA_ROOT), the console rejects any served
  // root that does not match before trusting an agent's Noise key (see session.ts).
  async getCARootPubKey(): Promise<string> {
    const r = await this.request<CARootPubKey>('GET', '/ca/root-pubkey');
    return r.root_pubkey;
  }

  // ---- §3 Device groups ---------------------------------------------------
  async listDeviceGroups(): Promise<DeviceGroup[]> {
    const r = await this.request<{ groups: DeviceGroup[] }>('GET', '/device-groups');
    return r.groups ?? [];
  }
  createDeviceGroup(name: string): Promise<DeviceGroup> {
    return this.request('POST', '/device-groups', { name });
  }

  // ---- §3/§4 Sessions -----------------------------------------------------
  startSession(deviceId: string, mode: SessionMode): Promise<SessionStartResult> {
    return this.request('POST', '/sessions', { device_id: deviceId, mode });
  }
  // Web->app launch: mint a one-time token the console hands to the native helmhost
  // client via a helm:// URL. The server returns its own base URL + org slug (the
  // console can't know its slug) so the helm:// URL can be built.
  createLaunchToken(deviceId: string, mode: SessionMode): Promise<LaunchToken> {
    return this.request('POST', '/sessions/launch-token', { device_id: deviceId, mode });
  }
  async listSessions(): Promise<Session[]> {
    const r = await this.request<{ sessions: Session[] }>('GET', '/sessions');
    return r.sessions ?? [];
  }
  async getSession(id: string): Promise<SessionDetail> {
    // Detail composite: { session, events }. Flatten to SessionDetail.
    const r = await this.request<{ session: Session; events: SessionEvent[] }>(
      'GET',
      `/sessions/${id}`,
    );
    return { ...r.session, events: r.events ?? [] };
  }

  // ---- §6 Attended support codes ------------------------------------------
  createSupportCode(): Promise<SupportCode> {
    return this.request('POST', '/support-codes', {});
  }

  // ---- §7 Installers ------------------------------------------------------
  createInstaller(input: { label?: string; branding?: InstallerConfig['branding'] }): Promise<InstallerResult> {
    return this.request('POST', '/installers', input);
  }

  /**
   * Build the concrete download URL for a stamped, self-contained installer.
   *
   * The server now returns download_url already pointing at
   * /api/v1/installers/{token}/download. If a deployment still returns the older
   * shape, we fall back to constructing that path from installer_token so the
   * console keeps working across the server rollout. The per-request os/arch and
   * optional name/group are appended as query params (the server stamps the
   * embedded config from these).
   */
  installerDownloadUrl(
    result: InstallerResult,
    opts: { os: string; arch?: string; name?: string; group?: string; format?: string },
  ): string {
    const isNewForm = /\/installers\/[^/]+\/download$/.test(result.download_url);
    const base = isNewForm
      ? result.download_url
      : `${API_BASE}${PREFIX}/installers/${result.installer_token}/download`;
    const qs = new URLSearchParams({ os: opts.os, arch: opts.arch ?? 'amd64' });
    if (opts.name) qs.set('name', opts.name);
    if (opts.group) qs.set('group', opts.group);
    if (opts.format && opts.format !== 'exe') qs.set('format', opts.format);
    return `${base}?${qs.toString()}`;
  }

  // ---- §5 TURN creds ------------------------------------------------------
  getTurnCredentials(): Promise<TurnCredentials> {
    return this.request('GET', '/turn-credentials');
  }

  // ---- §8 Audit -----------------------------------------------------------
  // Server returns { events, verify } (the hash-chain verification result rides
  // alongside the events). We surface both so the UI can show chain integrity.
  async listAudit(
    query: { from?: string; to?: string; action?: string; org_id?: string } = {},
  ): Promise<{ events: AuditEvent[]; verify: AuditVerify }> {
    const r = await this.request<{ events: AuditEvent[]; verify: AuditVerify }>(
      'GET',
      '/audit',
      undefined,
      { query },
    );
    return { events: r.events ?? [], verify: r.verify };
  }

  // ---- §3 Recordings ------------------------------------------------------
  getRecording(id: string): Promise<Recording> {
    return this.request('GET', `/recordings/${id}`);
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
